/**
 * Evidencias del canal de denuncias: una sola fuente para el texto que ve
 * el denunciante, el `accept` del input, la validación antes de subir y el
 * tipo con el que el investigador abre el archivo.
 *
 * Es la misma lista blanca que exige el servidor (EvidenciaDenuncia.java):
 * imágenes (jpg, jpeg, png, gif, webp), Word (doc, docx), Excel (xls, xlsx)
 * y PDF, hasta 10 MB por archivo. El servidor además revisa la firma de los
 * bytes; aquí solo se evita que el denunciante suba algo que se va a
 * rechazar.
 */

export const EVIDENCIA_MIME: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  pdf: 'application/pdf'
};

/** Extensiones y MIME: con los MIME el teléfono ofrece la galería de fotos. */
export const EVIDENCIA_ACCEPT: string = [
  ...Object.keys(EVIDENCIA_MIME).map(ext => '.' + ext),
  ...Array.from(new Set(Object.values(EVIDENCIA_MIME)))
].join(',');

export const EVIDENCIA_TAMANIO_MAX = 10 * 1024 * 1024;

/** El texto que la pantalla pública muestra SIEMPRE junto al archivo. */
export const EVIDENCIA_TEXTO =
  'Formatos aceptados: imágenes (JPG, PNG, GIF, WEBP), Word (DOC, DOCX), Excel (XLS, XLSX) y PDF. Máximo 10 MB por archivo.';

/** Resumen corto para la tarjeta cuando aún no hay archivo. */
export const EVIDENCIA_RESUMEN = 'JPG, PNG, GIF, WEBP, DOC, DOCX, XLS, XLSX, PDF · máx. 10 MB';

export function extensionDe(nombre: string | null | undefined): string {
  const limpio = (nombre || '').split(/[\\/]/).pop() || '';
  const punto = limpio.lastIndexOf('.');
  return punto < 0 ? '' : limpio.substring(punto + 1).trim().toLowerCase();
}

/** null si se acepta; si no, el mensaje que ve el denunciante. */
export function validarEvidencia(archivo: File): string | null {
  const ext = extensionDe(archivo?.name);
  if (!EVIDENCIA_MIME[ext]) {
    return `El archivo «${archivo?.name || ''}» no está permitido. ${EVIDENCIA_TEXTO}`;
  }
  if (!archivo.size) {
    return `El archivo «${archivo.name}» está vacío.`;
  }
  if (archivo.size > EVIDENCIA_TAMANIO_MAX) {
    return `El archivo «${archivo.name}» pesa ${formatoTamanio(archivo.size)}. ${EVIDENCIA_TEXTO}`;
  }
  return null;
}

export function formatoTamanio(bytes: number): string {
  if (!bytes) { return '0 B'; }
  if (bytes < 1024) { return bytes + ' B'; }
  if (bytes < 1048576) { return (bytes / 1024).toFixed(1) + ' KB'; }
  return (bytes / 1048576).toFixed(1) + ' MB';
}

/**
 * Cómo se abre una evidencia en el detalle. Solo por extensión (la que
 * validó el servidor, o la del nombre): nunca por el tipo que el navegador
 * declaró al subirla. Word y Excel no se previsualizan: se descargan.
 */
export function tipoVisorDe(extension: string): 'imagen' | 'pdf' | 'otro' {
  const ext = (extension || '').toLowerCase();
  if (/^(jpg|jpeg|png|gif|webp)$/.test(ext)) { return 'imagen'; }
  if (ext === 'pdf') { return 'pdf'; }
  return 'otro';
}

/** Lo que responde el alta pública: el código y el token para adjuntar la evidencia. */
export interface RespuestaAltaDenuncia {
  pCodRetorno: string;
  pMensaje: string;
  codigoSeguimiento: string;
  tokenCarga?: string;
}

/** Lo único que devuelve la consulta pública de estado. */
export interface EstadoPublicoDenuncia {
  pCodRetorno: string;
  pMensaje: string;
  codigoSeguimiento: string;
  id_estatus: number;
  nombreEstatus: string;
  fechaRegistro: string | Date;
  fechaUltimaActualizacion: string | Date;
}
